import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Link,
    List,
    ListItem,
    Pagination,
    Typography,
} from '@deere/fuel-react';
import { Breadcrumbs } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertBox } from '../../components/AlertBox.jsx';
import { CopyJobAndPasteAgain } from '../../components/CopyJobAndPasteAgain';
import { JobDetails } from '../../components/JobDetails.jsx';
import { AddBidderModal } from '../../components/admin-modals/AddBidderModal.jsx';
import { LoggedInContext } from '../../context/LoggedInContext';
import { ALLOWED_BIDDING_GROUPS_TO_WITHDRAW } from '../../constants';
import { errorBannerFromResponse } from '../../utils/errorBanner';
import styles from './ActiveJobDetails.module.css';
import { WithdrawEmployeeBidModal } from '../../components/admin-modals/WithdrawEmployeeBidModal';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { useGoBack } from '../../hooks/useGoBack';

export const ActiveJobDetails = () => {
    const { jobNumber } = useParams();
    const [bid, setBid] = useState();
    const [bidders, setBidders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [alertMsg, setAlertMsg] = useState();
    const [alertVariant, setAlertVariant] = useState(false);
    const [addBidModalOpen, setAddBidModalOpen] = useState(false);
    const bidderOptions = 'active';
    const [allowWithdraw, setAllowWithdraw] = useState(false);
    const { makeRequest, authState } = useContext(LoggedInContext);
    const goBack = useGoBack('/active-jobs');

    // Pagination starts
    const itemsPerPage = 10;
    const noOfPages = Math.ceil(bidders.length / itemsPerPage);
    const [page, setPage] = React.useState(1);
    const handleChange = (event, value) => {
        setPage(value);
    };
    // Pagination ends

    // Sorting starts
    const comparator = (a, b) => {
        const nameA = a.name?.toUpperCase(); // ignore upper and lowercase
        const nameB = b.name?.toUpperCase(); // ignore upper and lowercase
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }
        if (nameA === nameB) {
            const seniorityA = a.seniorityDate;
            const seniorityB = b.seniorityDate;
            if (seniorityA < seniorityB) {
                return -1;
            }
            if (seniorityA > seniorityB) {
                return 1;
            }
        }
        return 0;
    };
    // Sorting ends

    const addBidderToJob = async (bidder) => {
        const requestOptions = {
            method: 'POST',
            body: JSON.stringify(bidder),
        };
        try {
            const response = await makeRequest(
                process.env.REACT_APP_API_ENDPOINT +
                    '/job/' +
                    jobNumber +
                    '/bidders',
                requestOptions
            );
            if (response.status === 200) {
                const bidderResponse = await response.json();
                setAddBidModalOpen(false);
                setBidders([...bidders, bidderResponse]);
                setAlertVariant('success');
                setAlertMsg(
                    // prettier-ignore
                    'You have added a new bidder to the list! The new bidder\'s name will appear in alphabetical order by first name on the active bidder list.'
                );
            } else {
                setAddBidModalOpen(false);
                setAlertVariant('error');
                setAlertMsg(
                    errorBannerFromResponse(
                        response,
                        'Cannot add duplicate bidder. This bidder is already added in this job.'
                    ).message
                );
            }
        } catch (err) {
            setAlertVariant('error');
            setAlertMsg(
                errorBannerFromResponse(
                    err,
                    'We are unable to add bidders to this job. Please try again later.'
                ).message
            );
        }
    };

    const refreshActiveList = (racf) => {
        setBidders(bidders.filter((bidder) => bidder.racf !== racf));
    };

    useEffect(() => {
        let ignore = false;
        const fetchActiveBid = async () => {
            setIsLoading(true);
            try {
                if (!ignore) {
                    const bidResponse = await makeRequest(
                        `${process.env.REACT_APP_API_ENDPOINT}/jobs/${jobNumber}`
                    );
                    if (bidResponse.status === 200) {
                        const bidDetails = await bidResponse.json();
                        // Add the fix here to handle CLOSED jobPhase
                        if (!bidDetails || bidDetails.jobPhase === 'CLOSED') {
                            setAlertMsg(
                                'No active job found for the given job number.'
                            );
                            setAlertVariant('info');
                            setIsLoading(false);
                            return;
                        }
                        setBid(bidDetails);
                        setAllowWithdraw(
                            ALLOWED_BIDDING_GROUPS_TO_WITHDRAW.includes(
                                bidDetails.biddingGroupId
                            )
                        );
                        setIsLoading(false);
                    } else {
                        setAlertMsg(
                            errorBannerFromResponse(
                                bidResponse,
                                'Due to technical issues, we are unable to retrieve this job bid. Please try again later.'
                            ).message
                        );
                        setAlertVariant('error');
                        setIsLoading(false);
                    }
                }
            } catch (err) {
                setAlertMsg(
                    errorBannerFromResponse(
                        err,
                        'Due to technical issues, we are unable to retrieve this job bid. Please try again later.'
                    ).message
                );
                setAlertVariant('error');
            }
        };

        const fetchBidders = async () => {
            setIsLoading(true);
            try {
                if (!ignore) {
                    const response = await makeRequest(
                        process.env.REACT_APP_API_ENDPOINT +
                            '/job/' +
                            jobNumber +
                            '/bidders?bidderOptions=' +
                            bidderOptions
                    );
                    if (response.status === 200) {
                        const activeBidders = await response.json();
                        setBidders(activeBidders);
                        setIsLoading(false);
                    } else {
                        setAlertMsg(
                            errorBannerFromResponse(
                                response,
                                'Due to technical issues, we are unable to retrieve active bidders for this job. Please try again later.'
                            ).message
                        );
                        setAlertVariant('error');
                    }
                }
            } catch (err) {
                setAlertMsg(
                    errorBannerFromResponse(
                        err,
                        'Due to technical issues, we are unable to retrieve active bidders for this job. Please try again later.'
                    ).message
                );
                setAlertVariant('error');
            }
        };
        fetchActiveBid();
        fetchBidders();
        // cleanup function that will ignore future results of fetch triggered by the useEffect hook
        return () => {
            ignore = true;
        };
    }, [jobNumber, authState, makeRequest]);

    return (
        <div className={styles.pageLevelParent}>
            <Box spacing={6} className={styles.pageLevelContent}>
                <Breadcrumbs aria-label="Breadcrumbs">
                    <Link href="/active-jobs" onClick={goBack}>
                        Active Job Bids
                    </Link>
                    <Typography color="textPrimary">Job {jobNumber}</Typography>
                </Breadcrumbs>
                <Box className={styles.spaceOnTop}>
                    {alertVariant && (
                        <AlertBox msg={alertMsg} variant={alertVariant} />
                    )}
                </Box>
                {!isLoading && bid && (
                    <Box className={styles.boxForButton}>
                        <Typography
                            variant="h1"
                            className={styles.pageLevelHeading}
                        >
                            #{jobNumber}
                        </Typography>
                        <CopyJobAndPasteAgain bidobj={bid} />
                    </Box>
                )}
                {isLoading && (
                    <CircularProgress
                        size="lg"
                        variant="indeterminate"
                        title={'Loading'}
                    />
                )}
                {!isLoading && bid && <JobDetails bidObj={bid} />}
                {!isLoading && bid && (
                    <>
                        <Grid
                            container
                            spacing={4}
                            className={styles.biddersGridSection}
                        >
                            <Grid item sm={12}>
                                <Typography
                                    fontWeight="semibold"
                                    variant="h4"
                                    color="#367C2B"
                                >
                                    Active Bidders ({bidders.length})
                                </Typography>
                            </Grid>

                            <List style={{ width: '100%' }}>
                                {bidders
                                    .sort(comparator)
                                    .slice(
                                        (page - 1) * itemsPerPage,
                                        page * itemsPerPage
                                    )
                                    .map((bidder, index) => {
                                        return (
                                            <ListItem
                                                key={index}
                                                className={
                                                    styles.activeBiddersRow
                                                }
                                            >
                                                <Grid
                                                    container
                                                    className={
                                                        styles.activeBiddersRowInner
                                                    }
                                                >
                                                    <Grid
                                                        item
                                                        xs={6}
                                                        className={
                                                            styles.activeBiddersName
                                                        }
                                                    >
                                                        <Link
                                                            href={`/employee-info/${bidder.racf}/${jobNumber}`}
                                                        >
                                                            {bidder.name} -{' '}
                                                            {bidder.racf}
                                                        </Link>
                                                    </Grid>
                                                    <Grid
                                                        item
                                                        xs={6}
                                                        className={
                                                            styles.activeBiddersActionsSection
                                                        }
                                                    >
                                                        {allowWithdraw && (
                                                            <WithdrawEmployeeBidModal
                                                                bidderObj={
                                                                    bidder
                                                                }
                                                                withdrawnBid={
                                                                    refreshActiveList
                                                                }
                                                            />
                                                        )}
                                                    </Grid>
                                                    <Grid
                                                        item
                                                        xs={12}
                                                        className={
                                                            styles.activeBiddersInfoSection
                                                        }
                                                    >
                                                        <span>
                                                            <b>Dept: </b>
                                                            {bidder.dept}
                                                        </span>
                                                        <span>
                                                            <b>Seniority: </b>
                                                            {bidder.seniorityDate ? (
                                                                DateTime.fromISO(
                                                                    bidder.seniorityDate
                                                                ).toFormat(
                                                                    'LL/dd/yy'
                                                                )
                                                            ) : (
                                                                <span
                                                                    className={
                                                                        styles.nullSeniority
                                                                    }
                                                                >
                                                                    <ReportProblemIcon
                                                                        className={
                                                                            styles.nullSeniorityAlertIcon
                                                                        }
                                                                    />
                                                                    {'null'}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </Grid>
                                                </Grid>
                                            </ListItem>
                                        );
                                    })}
                            </List>
                        </Grid>
                        <Box className={styles.boxForButton}>
                            <AddBidderModal
                                jobNumber={jobNumber}
                                addBidderToJob={addBidderToJob}
                                closeModal={() => setAddBidModalOpen(false)}
                                modalIsOpen={addBidModalOpen}
                            />
                            <Button
                                variant="secondary"
                                onClick={() => setAddBidModalOpen(true)}
                                style={{ marginRight: '0.5rem' }}
                            >
                                + Add a Bidder to This Job
                            </Button>
                            <Pagination
                                sx={{ display: 'inline-table' }}
                                count={noOfPages}
                                page={page}
                                onChange={handleChange}
                                breakpoint={900}
                            />
                        </Box>
                    </>
                )}
            </Box>
        </div>
    );
};
